import { createContext, useContext, useEffect, useState } from "react";
import { SkeletonTheme } from "react-loading-skeleton";

export type Theme = "dark" | "light" | "system";

interface ThemeProviderProps {
  children: React.ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
}

interface ThemeProviderState {
  /** The user's choice. "system" follows the computer's light or dark setting. */
  theme: Theme;
  /** What is shown right now. */
  resolvedTheme: "dark" | "light";
  setTheme: (theme: Theme) => void;
}

const initialState: ThemeProviderState = {
  theme: "light",
  resolvedTheme: "light",
  setTheme: () => null,
};

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

const darkSchemeQuery = "(prefers-color-scheme: dark)";

export function ThemeProvider({
  children,
  // Dark mode is in beta, so only users who pick it (or "system") get it.
  defaultTheme = "light",
  storageKey = "ui-theme",
  ...props
}: ThemeProviderProps) {
  const [theme, setTheme] = useState<Theme>(
    () => (localStorage.getItem(storageKey) as Theme) || defaultTheme,
  );
  const [systemIsDark, setSystemIsDark] = useState(
    () => window.matchMedia(darkSchemeQuery).matches,
  );

  useEffect(() => {
    const query = window.matchMedia(darkSchemeQuery);
    const handleChange = () => setSystemIsDark(query.matches);

    query.addEventListener("change", handleChange);
    return () => query.removeEventListener("change", handleChange);
  }, []);

  const resolvedTheme =
    theme === "system" ? (systemIsDark ? "dark" : "light") : theme;

  useEffect(() => {
    const root = window.document.documentElement;

    root.classList.add("theme-switching");
    root.classList.remove("light", "dark");
    root.classList.add(resolvedTheme);
    // Two frames: the first paints the new colours without transitions.
    requestAnimationFrame(() =>
      requestAnimationFrame(() => root.classList.remove("theme-switching")),
    );
  }, [resolvedTheme]);

  const value = {
    theme,
    resolvedTheme,
    setTheme: (theme: Theme) => {
      localStorage.setItem(storageKey, theme);
      setTheme(theme);
    },
  };

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      <SkeletonTheme enableAnimation>{children}</SkeletonTheme>
    </ThemeProviderContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext);

  if (context === undefined)
    throw new Error("useTheme must be used within a ThemeProvider");

  return context;
};
